/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { func, object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ErrorIcon from '@material-ui/icons/Error';

import configENV from '../../../config';
import { getPriceDiffLabel } from '../helper';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const { PERSIST_KEY } = configENV;

const useStyles = makeStyles((theme) => ({
    container: {
        height: '100%',
        padding: '17px 17px 27px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '& .survey_text': {
            textAlign: 'center',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '26px', /* 162.5% */
            margin: '34px 0 66px',
        },
    },
    '@keyframes stroke': {
        '100%': {
            strokeDashoffset: '0',
        },
    },
    '@keyframes scale': {
        '0%, 100%': {
            transform: 'none',
        },
        '50%': {
            transform: 'scale3d(1.1, 1.1, 1)',
        },
    },
    '@keyframes fill': {
        '100%': {
            boxShadow: `inset 0px 0px 0px 40px ${theme?.palette?.primaryButton}`,
        },
    },
    checkMarkCircle: {
        strokeDasharray: '166',
        strokeDashoffset: '166',
        strokeWidth: '2',
        strokeMiterlimit: '10',
        stroke: theme?.palette?.primaryButton,
        fill: 'none',
        animation: '$stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards',
    },
    checkMark: {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        display: 'inline-block',
        strokeWidth: 4,
        stroke: theme?.palette?.white,
        strokeMiterlimit: '10',
        margin: '14px 5px 14px 0',
        verticalAlign: 'middle',
        boxShadow: `inset 0px 0px 0px ${theme?.palette?.primaryButton}`,
        animation: '$fill .4s ease-in-out .4s forwards, $scale .3s ease-in-out .9s both',
    },
    checkMarkCheck: {
        transformOrigin: '50% 50%',
        strokeDasharray: '38',
        strokeDashoffset: '5',
        animation: '$stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards',
    },
    btnContainer: {
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        '@media screen and (max-width: 425px)': {
            flexDirection: 'column',
        },
        '& button': {
            display: 'flex',
            background: 'transparent',
            border: `1px solid ${theme.palette?.colorPrimary || '#201648'}`,
            borderRadius: '3px',
            color: theme.palette?.colorPrimary || '#201648',
            padding: '8px 20px',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            textAlign: 'center',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '24px', /* 142.857% */
            letterSpacing: '0.64px',
            cursor: 'pointer',
            textTransform: 'capitalize',
            boxShadow: 'none',
            '@media screen and (max-width: 425px)': {
                width: '100%',
            },
        },
        '& .select_btn': {
            background: '#201648 !important',
            color: '#fff !important',
            marginLeft: '11px',
            '@media screen and (max-width: 425px)': {
                marginTop: '12px',
                marginLeft: 0,
            },
        },
    },
}));

const SwapSubscriptionConfirmation = ({
    toggle, data, close, copy, subscription, setPayload,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const hasError = useSelector((state) => state[PERSIST_KEY]?.swapSubscriptionResponse?.isError || false);
    const hasSwapped = useSelector((state) => state[PERSIST_KEY]?.swapSubscriptionResponse?.isSuccess || false);
    const [loading, setLoading] = useState(false);
    const [submittedRequest, setSubmittedRequest] = useState(false);
    const closeTimeout = useRef(null);

    const handleContinue = () => {
        dispatch(EventDispatcher.handleSwapSubscription(data));
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: 'Yes, Change',
        }));
        setLoading(true);
    };
    const diff = getPriceDiffLabel(Number(subscription.price), Number(data.data?.productPrice));
    let confirmationCopy = '';
    if (diff.includes('-')) confirmationCopy = copy?.decremented_price_copy.replace('<<PRICE>>', diff).replace('-', '');
    else if (diff.includes('+')) confirmationCopy = copy?.incremented_price_copy.replace('<<PRICE>>', diff).replace('+', '');
    else confirmationCopy = copy?.same_price_copy.replace('<<PRICE>>', `$${subscription.price}`);

    const successMessage = copy?.success_copy ? copy.success_copy : 'Your subscription order is updated.';
    const errorMessage = copy?.error_copy ? copy.error_copy : 'Something went wrong and we are unable to process your request at the moment. Please try again later.';

    const handleCancelChange = () => {
        close();
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: 'No, Don’t Change',
        }));
    };

    const handleClose = () => {
        clearTimeout(closeTimeout.current);
        toggle();
    };

    useEffect(() => {
        if (hasSwapped || hasError) {
            setSubmittedRequest(true);
            if (hasSwapped) {
                setLoading(false);
                setPayload({});
                dispatch(EventDispatcher.clearSwapSubscription());
                closeTimeout.current = setTimeout(() => {
                    toggle();
                    setSubmittedRequest(false);
                }, 2500);
            }
        }
        return () => {
            clearTimeout(closeTimeout.current);
        };
    }, [hasSwapped, hasError]);

    const renderResult = () => (
        <>
            {hasError ? (
                <>
                    <ErrorIcon color="error" fontSize="large" style={{ alignSelf: 'center' }} />
                    <p className="survey_text" style={{ color: 'red' }}>{errorMessage}</p>
                </>
            )
                : (
                    <>
                        <div>
                            <svg
                                className={classes.checkMark}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 52 52"
                            >
                                <circle className={classes.checkMarkCircle} cx="26" cy="26" r="25" fill="none" />
                                <path className={classes.checkMarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </svg>
                        </div>
                        <p className="survey_text">{successMessage}</p>
                    </>
                )}
            <div className={classes.btnContainer}>
                <Button onClick={handleClose}>Close</Button>
            </div>
        </>
    );

    return (
        <div className={classes.container}>
            {submittedRequest ? renderResult() : (
                <>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="135" height="130" viewBox="0 0 135 130" fill="none">
                            <path d="M87.2337 51.2587C87.4933 51.5183 87.8186 51.6328 88.1599 51.6328C88.4852 51.6328 88.8264 51.5024 89.086 51.2587L98.4634 41.8814C99.7478 40.5969 100.462 38.875 100.462 37.0542C100.462 35.2336 99.7467 33.5276 98.4634 32.2271C95.8798 29.6436 91.3941 29.6594 88.8263 32.2271L88.1597 32.8937L87.4931 32.2271C84.9254 29.6594 80.4395 29.6436 77.856 32.2271C76.5716 33.5116 75.8574 35.2335 75.8574 37.0542C75.8574 38.8749 76.5727 40.5809 77.856 41.8814L87.2337 51.2587ZM79.6768 34.0807C80.4726 33.2849 81.5292 32.8451 82.6503 32.8451C83.7885 32.8451 84.8439 33.2838 85.641 34.0807L87.2179 35.6576C87.7212 36.161 88.5511 36.161 89.0544 35.6576L90.6313 34.0807C92.2241 32.488 94.9868 32.4721 96.5954 34.0807C97.3913 34.8765 97.8311 35.9331 97.8311 37.0542C97.8311 38.1754 97.3924 39.232 96.5954 40.0449L88.1282 48.5121L79.661 40.0449C78.8651 39.2491 78.4253 38.1925 78.4253 37.0542C78.4412 35.9331 78.8799 34.8765 79.6768 34.0807Z" fill="black" />
                            <path d="M58.6301 30.2298H69.9574C70.6728 30.2298 71.2577 29.6449 71.2577 28.9295V17.6179C71.2577 14.3019 68.5597 11.605 65.2448 11.605C63.6362 11.605 62.1239 12.2228 61.0028 13.3599C59.8646 14.4981 59.2479 16.0092 59.2479 17.6019V18.2027H58.6301C57.0214 18.2027 55.5262 18.8206 54.388 19.9576C53.2498 21.0958 52.6331 22.6069 52.6331 24.1997C52.6161 27.5313 55.3142 30.2294 58.6301 30.2294L58.6301 30.2298ZM56.2087 21.8113C56.8583 21.1617 57.7039 20.8035 58.6143 20.8035H60.5154C61.2307 20.8035 61.8157 20.2185 61.8157 19.5032V17.6021C61.8157 16.6918 62.1728 15.8302 62.8065 15.1965C63.4561 14.5628 64.3018 14.2057 65.2121 14.2057C67.0815 14.2057 68.6256 15.7338 68.6256 17.6191L68.6245 27.6292H58.5973C56.7279 27.6292 55.1838 26.101 55.1838 24.2157C55.2167 23.3065 55.575 22.4609 56.2087 21.8113Z" fill="black" />
                            <path d="M106.152 28.9785H115.709C118.618 28.9785 120.991 26.6057 120.991 23.6969C120.991 22.2832 120.439 20.9671 119.447 19.9592C118.456 18.9684 117.123 18.4152 115.709 18.4152H115.401V18.1069C115.401 16.6932 114.849 15.3771 113.857 14.3692C112.866 13.3784 111.533 12.8252 110.119 12.8252C107.21 12.8252 104.837 15.1979 104.837 18.1068V27.6635C104.852 28.4094 105.437 28.9785 106.152 28.9785ZM107.453 18.1228C107.453 16.6433 108.655 15.4417 110.134 15.4417C110.849 15.4417 111.516 15.7183 112.019 16.2216C112.522 16.725 112.799 17.4086 112.799 18.1228V19.7314C112.799 20.4467 113.384 21.0317 114.099 21.0317H115.708C116.423 21.0317 117.09 21.3083 117.609 21.8117C118.112 22.315 118.389 22.9816 118.389 23.6969C118.389 25.1763 117.186 26.378 115.708 26.378H107.452L107.453 18.1228Z" fill="black" />
                            <path d="M43.4028 88.5911C43.2894 88.7044 43.2078 88.8348 43.159 88.9652C43.1432 88.981 43.1262 88.9969 43.1262 89.0128C43.0615 89.1749 43.0287 89.3382 43.0287 89.5161C43.0287 89.5978 43.0615 89.6624 43.0774 89.744C43.0933 89.8256 43.0933 89.9231 43.1262 90.0036C43.1908 90.1657 43.2883 90.312 43.4028 90.4264C43.4515 90.4752 43.5161 90.4911 43.5649 90.5398C43.6465 90.6044 43.727 90.6702 43.8245 90.7019L56.3387 95.8703V116.218C56.3387 116.787 56.6958 117.259 57.2003 117.437L87.3476 129.869C87.5097 129.951 87.7047 130 87.8997 130C88.0947 130 88.2897 129.951 88.4518 129.869L118.599 117.437C119.102 117.258 119.461 116.787 119.461 116.218L119.462 95.8871L131.976 90.7188C132.074 90.67 132.155 90.6054 132.236 90.5396C132.284 90.5068 132.349 90.475 132.398 90.4263C132.528 90.2959 132.609 90.1667 132.674 90.0034C132.707 89.9218 132.707 89.8413 132.723 89.7438C132.739 89.6622 132.772 89.5976 132.772 89.516C132.772 89.3539 132.739 89.1747 132.674 89.0126C132.658 88.9797 132.626 88.9639 132.61 88.931C132.545 88.8176 132.479 88.6873 132.382 88.5898L119.965 76.173L132.382 63.7563C132.512 63.6259 132.593 63.4797 132.658 63.3334C132.723 63.1713 132.756 63.0081 132.756 62.8301C132.756 62.7485 132.723 62.668 132.707 62.6022C132.691 62.5206 132.691 62.4231 132.658 62.3426C132.594 62.1805 132.496 62.0343 132.382 61.9198C132.333 61.871 132.268 61.8552 132.22 61.8064C132.138 61.7418 132.058 61.676 131.96 61.6273L101.717 49.1458C101.555 49.0812 101.392 49.0483 101.214 49.0483C101.149 49.0483 101.083 49.0812 101.035 49.0812C100.937 49.0971 100.824 49.0971 100.726 49.1458C100.564 49.2105 100.434 49.3079 100.303 49.4224L87.8867 61.8392L75.4699 49.4224C75.3566 49.3091 75.2103 49.2116 75.0471 49.1458C74.9496 49.0971 74.8362 49.0971 74.7387 49.0812C74.6741 49.0653 74.6254 49.0483 74.5596 49.0483C74.3975 49.0483 74.2184 49.0812 74.0563 49.1458L43.7958 61.6273C43.6983 61.6602 43.6167 61.7407 43.5362 61.7894C43.4875 61.8223 43.4228 61.854 43.3741 61.9028C43.2607 62.0331 43.1632 62.1624 43.0975 62.3256C43.0646 62.4072 43.0646 62.5047 43.0487 62.5852C43.0329 62.6668 43 62.7315 43 62.8131C43 62.9752 43.0329 63.1543 43.0975 63.3164C43.1621 63.4627 43.2596 63.6089 43.3741 63.7393L55.7908 76.156L43.4028 88.5911ZM86.6154 126.766L58.9547 115.357V96.9599L74.1012 103.201C74.2633 103.265 74.4265 103.298 74.5886 103.298C74.8165 103.298 75.0274 103.217 75.2223 103.103C75.2711 103.07 75.3198 103.039 75.3686 103.006C75.4173 102.973 75.4661 102.957 75.5148 102.908L86.6314 91.7916L86.6326 126.765L86.6154 126.766ZM116.876 115.357L89.2151 126.766V91.8066L100.332 102.923C100.38 102.972 100.429 102.988 100.478 103.021C100.527 103.054 100.575 103.085 100.624 103.118C100.819 103.232 101.03 103.313 101.258 103.313C101.42 103.313 101.599 103.28 101.745 103.216L116.892 96.975V115.356L116.876 115.357ZM129.211 89.0609L101.55 100.47L90.2059 89.1254L117.867 77.7165L129.211 89.0609ZM101.55 51.8934L129.211 63.3022L117.867 74.6466L90.2059 63.2378L101.55 51.8934ZM114.763 76.1737L87.9147 87.2416L61.0666 76.1737L87.9147 65.1059L114.763 76.1737ZM46.618 63.3022L74.2788 51.8934L85.6232 63.2378L57.9624 74.6466L46.618 63.3022ZM57.9624 77.7177L85.6232 89.1265L74.2788 100.471L46.618 89.0609L57.9624 77.7177Z" fill="black" />
                            <path d="M78.3445 12.4009C83.0413 12.4009 86.8445 16.2201 86.8445 20.901C86.8445 21.6163 87.4295 22.2013 88.1448 22.2013C88.8601 22.2013 89.4451 21.6163 89.4451 20.901C89.4451 16.2042 93.2643 12.4009 97.9452 12.4009C98.6605 12.4009 99.2455 11.816 99.2455 11.1006C99.2455 10.3853 98.6605 9.80033 97.9452 9.80033C93.2484 9.80033 89.4451 5.98114 89.4451 1.3003C89.4451 0.584982 88.8602 0 88.1448 0C87.4295 0 86.8445 0.584953 86.8445 1.3003C86.8445 5.9971 83.0253 9.80033 78.3445 9.80033C77.6292 9.80033 77.0442 10.3853 77.0442 11.1006C77.0442 11.833 77.6303 12.4009 78.3445 12.4009ZM88.145 6.51832C89.1856 8.4682 90.7943 10.0781 92.7443 11.1176C90.7944 12.1583 89.1845 13.7669 88.145 15.7169C87.1043 13.767 85.4956 12.1572 83.5457 11.1176C85.4956 10.0611 87.1043 8.46829 88.145 6.51832Z" fill="black" />
                            <path d="M48.2959 43.3129C51.5947 43.3129 54.2929 45.994 54.2929 49.3098C54.2929 50.0252 54.8778 50.6101 55.5932 50.6101C56.3085 50.6101 56.8935 50.0252 56.8935 49.3098C56.8935 46.011 59.5745 43.3129 62.8904 43.3129C63.6057 43.3129 64.1907 42.7279 64.1907 42.0126C64.1907 41.2973 63.6058 40.7123 62.8904 40.7123C59.5916 40.7123 56.8935 38.0312 56.8935 34.7153C56.8935 34 56.3085 33.415 55.5932 33.415C54.8778 33.415 54.2929 34 54.2929 34.7153C54.2929 38.0142 51.6118 40.7123 48.2959 40.7123C47.5806 40.7123 46.9956 41.2972 46.9956 42.0126C46.9956 42.7279 47.5647 43.3129 48.2959 43.3129ZM55.5773 39.267C56.2757 40.3723 57.2188 41.3144 58.3241 42.0138C57.203 42.7121 56.2598 43.6553 55.5773 44.7606C54.879 43.6395 53.9358 42.6963 52.8305 42.0138C53.9517 41.3144 54.8938 40.3723 55.5773 39.267Z" fill="black" />
                            <path d="M116.879 43.913C116.879 44.6283 117.464 45.2133 118.179 45.2133C121.527 45.2133 124.257 47.9431 124.257 51.2918C124.257 52.0071 124.842 52.5921 125.558 52.5921C126.273 52.5921 126.858 52.0071 126.858 51.2918C126.858 47.9442 129.588 45.2133 132.937 45.2133C133.652 45.2133 134.237 44.6283 134.237 43.913C134.237 43.1976 133.652 42.6127 132.937 42.6127C129.589 42.6127 126.858 39.8829 126.858 36.5342C126.858 35.8189 126.273 35.2339 125.558 35.2339C124.842 35.2339 124.257 35.8188 124.257 36.5342C124.257 39.8818 121.528 42.6127 118.179 42.6127C117.464 42.6127 116.879 43.1976 116.879 43.913ZM125.557 41.1015C126.272 42.2397 127.231 43.2146 128.368 43.913C127.23 44.6283 126.255 45.5874 125.557 46.7244C124.841 45.5862 123.882 44.6113 122.745 43.913C123.883 43.2135 124.858 42.2386 125.557 41.1015Z" fill="black" />
                            <circle cx="35" cy="88" r="34.5" fill="#E2F1EC" stroke="white" />
                            <path d="M17.9896 91.5241C16.2646 83.2446 20.7175 74.9713 28.5773 71.8525C35.7613 69.0009 43.8243 71.2374 48.5326 77.1474L44.4662 76.5446C43.9346 76.4676 43.4324 76.835 43.3523 77.3711C43.2729 77.9072 43.6431 78.406 44.1792 78.4854L50.4987 79.4221C50.5466 79.4289 50.5945 79.4324 50.6424 79.4324C50.8518 79.4324 51.0567 79.3653 51.2264 79.2391C51.4358 79.0841 51.574 78.8521 51.6123 78.5952L52.548 72.2761C52.6274 71.74 52.2572 71.2418 51.7211 71.1621C51.1864 71.0844 50.6869 71.4529 50.6072 71.989L50.031 75.8792C44.7848 69.3328 35.8333 66.8618 27.8528 70.0287C19.105 73.5008 14.149 82.7087 16.0688 91.924C16.1649 92.3869 16.5731 92.7054 17.0281 92.7054C17.0944 92.7054 17.1615 92.6985 17.2292 92.6848C17.7595 92.5743 18.0996 92.0547 17.9894 91.5244L17.9896 91.5241ZM53.8996 84.0722C53.7884 83.5416 53.2698 83.1991 52.7392 83.312C52.2089 83.4228 51.8685 83.9422 51.979 84.4724C53.7064 92.7547 49.2534 101.029 41.3913 104.148C34.2096 106.997 26.1487 104.761 21.4394 98.852L25.4931 99.4527C26.0295 99.5318 26.5274 99.1619 26.6071 98.6258C26.6864 98.0904 26.3163 97.5916 25.7802 97.5119L19.4586 96.5752C19.202 96.5383 18.9392 96.6026 18.7309 96.7582C18.5215 96.9132 18.3826 97.1452 18.345 97.4021L17.4107 103.723C17.3316 104.259 17.7021 104.757 18.2375 104.837C18.2865 104.844 18.3347 104.848 18.3826 104.848C18.8609 104.848 19.2796 104.497 19.3518 104.01L19.9289 100.104C23.6398 104.743 29.2073 107.342 34.9762 107.342C37.3608 107.342 39.7803 106.898 42.1152 105.972C50.8665 102.5 55.8234 93.2908 53.9001 84.072L53.8996 84.0722Z" fill="black" />
                        </svg>
                    </div>
                    <p className="survey_text">{confirmationCopy}</p>
                    <div className={classes.btnContainer}>
                        <Button disabled={loading} onClick={handleCancelChange}>No, Don’t Change</Button>
                        <Button disabled={loading} className="select_btn" onClick={handleContinue}>Yes, Change</Button>
                    </div>
                </>
            )}
        </div>
    );
};

SwapSubscriptionConfirmation.propTypes = {
    data: object.isRequired,
    copy: object.isRequired,
    subscription: object.isRequired,
    toggle: func.isRequired,
    close: func.isRequired,
    setPayload: func.isRequired,
};

SwapSubscriptionConfirmation.defaultProps = {
};

export default SwapSubscriptionConfirmation;
